import { Fragment } from 'react';

import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { authSlice } from 'src/features/auth/auth.slice';
import { authService } from 'src/features/auth/authService';
import { selectCurrentOrganization } from 'src/features/auth/selectors';
import { useUserRoles } from 'src/features/auth/useUserRoles';

import { ChangeOrganizationModal } from '../ChangeOrganizationModal';
import { Avatar } from '../primitives/Avatar';
import { useShouldShowOrgSettings } from './useShouldShowOrgSettings';

export function ProfileDropdown() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isBambi } = useUserRoles();

  const currentOrganization = useAppSelector(selectCurrentOrganization);
  const user = useAppSelector((state) => state.auth.profile);
  const shouldShowSettings = useShouldShowOrgSettings();

  if (!user) {
    throw new Error('User is required for ProfileDropdown');
  }

  const { first_name, last_name } = user;

  return (
    <Menu as="div" className="relative border-l">
      <Menu.Button data-testid="profile-drop-down-btn">
        <div className="hidden items-center gap-4 truncate pl-6 md:flex">
          <div className="flex max-w-[125px] flex-col text-left md:max-w-[175px]">
            <div className="truncate text-sm font-semibold">
              {currentOrganization?.name || ''}
            </div>
            <div className="truncate text-xs text-gray-700">
              {first_name} {last_name}
            </div>
          </div>
          <div>
            <ChevronDownIcon className="h-4 w-4 text-black" />
          </div>
        </div>
        <div className="flex items-center gap-4 pl-4 md:hidden">
          <Avatar firstName={first_name} lastName={last_name} />
        </div>

        <span className="sr-only">Open user menu</span>
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {shouldShowSettings && (
            <ProfileDropdownMenuItem>
              <button
                onClick={() => {
                  navigate('/settings');
                }}
                className={
                  'block w-full px-4 py-2 text-left text-sm text-gray-700'
                }
              >
                Settings
              </button>
            </ProfileDropdownMenuItem>
          )}
          {user.memberships.length > 1 && (
            <ProfileDropdownMenuItem>
              <button
                onClick={() => {
                  dispatch(authSlice.actions.setIsOrganizationChangeOpen(true));
                }}
                className={
                  'block w-full px-4 py-2 text-left text-sm text-gray-700'
                }
              >
                Change Organization
              </button>
            </ProfileDropdownMenuItem>
          )}
          {isBambi && (
            <ProfileDropdownMenuItem>
              <button
                className={
                  'block w-full px-4 py-2 text-left text-sm text-gray-700'
                }
                onClick={() => navigate('/admin')}
              >
                Admin
              </button>
            </ProfileDropdownMenuItem>
          )}
          <ProfileDropdownMenuItem>
            <button
              onClick={authService.signOut}
              className={
                'block w-full px-4 py-2 text-left text-sm text-gray-700'
              }
            >
              Sign out
            </button>
          </ProfileDropdownMenuItem>
        </Menu.Items>
      </Transition>
      <ChangeOrganizationModal />
    </Menu>
  );
}

function ProfileDropdownMenuItem({ children }: { children: React.ReactNode }) {
  return (
    <Menu.Item>
      <div className="w-full text-left hover:bg-mint-light hover:text-indigo">
        {children}
      </div>
    </Menu.Item>
  );
}
